/**
 * Did-you-mean domain suggestion for the capture field (spec to-do 13).
 *
 * THREE CONSTRAINTS, all load-bearing:
 *
 *   1. SUGGEST, never auto-correct. Auto-correcting a real address at a real
 *      domain we happen not to know is the one failure worse than a typo,
 *      because it is silent and she cannot see it happen.
 *   2. DOMAIN ONLY. The local part is unguessable by construction — there is no
 *      dictionary of correct local parts, so any "correction" there is a coin
 *      flip on data we have no claim to.
 *   3. THE HINT COLLAPSES when it cannot fire. A permanently-reserved row under
 *      the input is a layout hole; returning null and rendering nothing is the
 *      whole contract.
 *
 * Why this matters more here than on a normal signup form: a mistyped address
 * on the capture sheet fails COMPLETELY SILENTLY today. There is no password to
 * get wrong later, no bounce she ever sees, and the sheet says "Saved" the
 * instant she submits (deliberately — she must not wait on the callable). She
 * then waits for a link that was never going to arrive. This function and the
 * You card's show-the-address-back-in-full are the only two places that failure
 * is recoverable.
 */

/**
 * Domains worth defending. Deliberately short: every entry is a domain a typo
 * is likely to be NEAR, and a long tail only raises the false-positive rate
 * (suggesting "yahoo.com" at someone whose company domain is one edit away is a
 * worse experience than staying quiet).
 */
export const KNOWN_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'comcast.net',
  'verizon.net',
  'sbcglobal.net',
  'protonmail.com',
  'proton.me',
] as const;

/** Max edit distance we will suggest across. 2 catches gmial/gmai/gmail.con. */
const MAX_DISTANCE = 2;

/**
 * Below this length a distance-2 suggestion is mostly noise — "me.com" is two
 * edits from a lot of things. Short domains only get distance-1 treatment.
 */
const SHORT_DOMAIN_LEN = 8;

/** Standard Levenshtein. Small inputs, so the simple two-row form is plenty. */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = row;
  }
  return prev[b.length];
}

/**
 * Returns the full suggested address, or null when we should stay quiet.
 *
 * Null (i.e. the hint collapses) whenever:
 *   - the input has no single unambiguous `@`,
 *   - either side of the `@` is empty,
 *   - the domain is already one we know (nothing to suggest),
 *   - nothing is close enough to be worth interrupting for.
 */
export function suggestEmail(raw: string): string | null {
  const value = raw.trim();
  const at = value.indexOf('@');
  if (at <= 0) return null;
  // More than one @ is not a typo we can reason about.
  if (value.indexOf('@', at + 1) !== -1) return null;

  const local = value.slice(0, at);
  const domain = value.slice(at + 1).toLowerCase();
  if (!local || !domain) return null;
  if ((KNOWN_DOMAINS as readonly string[]).includes(domain)) return null;

  let best: string | null = null;
  let bestDistance = Infinity;
  for (const candidate of KNOWN_DOMAINS) {
    const distance = editDistance(domain, candidate);
    const limit = candidate.length < SHORT_DOMAIN_LEN ? 1 : MAX_DISTANCE;
    if (distance > limit) continue;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  if (!best) return null;
  return `${local}@${best}`;
}
